import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getGlobalSettings } from '@/api/globalSettings';
import type { AppSettings } from '@/global/globalState';
import { queryKeys } from '@/lib/queryKeys';
import type { GlobalSettingsView } from '@/models';
import { useCurrentSchool } from './useCurrentSchool';
import { useGlobalSelector, useGlobalState } from './useGlobalState';

/** How often an open session checks for changed settings. */
export const SETTINGS_REFRESH_MS = 5 * 60 * 1000;

const versionOf = (view: GlobalSettingsView, branchId: number) =>
  `${branchId}:${view.organizationVersion}.${view.schoolVersion}`;

/** The branch's (school's) resolved Global Settings in the shape the formatters read. */
export function toAppSettings(view: GlobalSettingsView, branchId: number): AppSettings {
  const { general, dateTime } = view.values;
  return {
    locale: general.defaultLanguage,
    currency: general.currency,
    dateFormat: dateTime.dateFormat,
    shortDateFormat: dateTime.shortDateFormat,
    monthDisplay: dateTime.monthDisplay,
    timeFormat: dateTime.timeFormat,
    showSeconds: dateTime.showSeconds,
    amPmStyle: dateTime.amPmStyle,
    timeZone: dateTime.timeZone,
    firstDayOfWeek: general.firstDayOfWeek,
    numberFormat: general.numberFormat,
    thousandSeparator: general.thousandSeparator,
    decimalSymbol: general.decimalSymbol,
    decimalPrecision: general.decimalPrecision,
    pageSizeList: general.pageSizeList,
    version: versionOf(view, branchId),
  };
}

/**
 * Keeps `appSettings` equal to the current school's Global Settings. Loaded when
 * the app starts and when the school changes (applied at once); afterwards it
 * re-checks every few minutes while the tab is open, and on a navigation once the
 * copy is that old - not on window focus, so working on a page (or switching to
 * another window and back) sends no settings requests. A change found in the
 * background is applied on the next navigation, so a page being edited is never
 * re-rendered with new formats under the user.
 */
export function useAppSettingsSync(): void {
  // Settings are kept per branch; the current school names it.
  const branchId = useCurrentSchool().currentSchool?.branchId ?? null;
  const applied = useGlobalSelector((s) => s.appSettings);
  const { setAppSettings } = useGlobalState();
  const { pathname } = useLocation();

  const { data, refetch, isStale } = useQuery({
    queryKey: queryKeys.globalSettings.settings(branchId),
    queryFn: () => getGlobalSettings(branchId),
    enabled: branchId != null,
    staleTime: SETTINGS_REFRESH_MS,
    refetchInterval: SETTINGS_REFRESH_MS,
    refetchOnWindowFocus: false,
  });

  const latest = useMemo(
    () => (data && branchId != null ? toAppSettings(data, branchId) : null),
    [data, branchId],
  );

  // The first answer after the app starts (the stored copy may be from an earlier
  // session), or another school: apply straight away.
  const loaded = useRef(false);
  useEffect(() => {
    if (!latest) return;
    const firstLoad = !loaded.current;
    loaded.current = true;
    if (latest.version === applied.version) return;
    if (firstLoad || applied.version.split(':')[0] !== String(branchId)) setAppSettings(latest);
  }, [latest, applied.version, branchId, setAppSettings]);

  // Navigation: apply a change found in the background, and look for a newer one.
  const onNavigate = useEffectEvent(() => {
    if (latest && latest.version !== applied.version) setAppSettings(latest);
    if (isStale) void refetch();
  });
  useEffect(() => {
    onNavigate();
  }, [pathname]);
}
